import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import DirectionsRunRoundedIcon from '@mui/icons-material/DirectionsRunRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import PixRoundedIcon from '@mui/icons-material/PixRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import CakeRoundedIcon from '@mui/icons-material/CakeRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import VolunteerActivismRoundedIcon from '@mui/icons-material/VolunteerActivismRounded';
import SectionCard from '../../components/ui/SectionCard';
import StatCard from '../../components/ui/StatCard';
import LoadingState from '../../components/ui/LoadingState';
import EmptyState from '../../components/ui/EmptyState';
import PlayerAvatar from '../../components/ui/PlayerAvatar';
import BarChart from '../../charts/bar';
import api from '../../api';
import tokens from '../../theme/tokens';
import { formatCurrency } from '../../utils/image';

// Visão geral da pelada para a diretoria.
const AdminDashboard = ({ onMessage, onGoTab }) => {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [confirmar, setConfirmar] = useState(null);
  const [abonando, setAbonando] = useState(false);

  const carregar = async () => {
    setCarregando(true);
    try {
      const response = await api.get('admin/dashboard');
      setDados(response.data);
    } catch (error) {
      onMessage && onMessage('Erro ao carregar o dashboard', 'error');
    }
    setCarregando(false);
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abonar = async () => {
    setAbonando(true);
    try {
      const ids = confirmar.meses.flatMap((m) => m.cobrancas.map((c) => c._id));
      const response = await api.post('admin/abonar', { userId: confirmar.user._id, cobrancaIds: ids, mudarParaDiarista: true });
      onMessage && onMessage(`${confirmar.user.nome}: ${response.data.abonadas} cobrança(s) abonada(s)${response.data.virouDiarista ? ' e agora é diarista' : ''}.`);
      setConfirmar(null);
      await carregar();
    } catch (error) {
      onMessage && onMessage(error.response?.data?.error || 'Erro ao abonar', 'error');
    }
    setAbonando(false);
  };

  if (carregando && !dados) return <LoadingState label="Montando o dashboard..." />;
  if (!dados) return <EmptyState title="Dashboard indisponível" action={<Button onClick={carregar}>Tentar novamente</Button>} />;

  const { usuarios, financeiro, peladas, abono, aniversariantes } = dados;

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">Atualizado em {new Date(dados.geradoEm).toLocaleString('pt-BR')}</Typography>
        <Tooltip title="Atualizar"><IconButton onClick={carregar}><RefreshRoundedIcon /></IconButton></Tooltip>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard label="Saldo em caixa" value={formatCurrency(financeiro.saldoCaixa)} hint={`+${formatCurrency(financeiro.entradasMes)} / -${formatCurrency(financeiro.saidasMes)} no mês`} icon={<AccountBalanceWalletRoundedIcon />} tone={financeiro.saldoCaixa < 0 ? 'danger' : 'success'} />
        </Grid>
        <Grid item xs={6} sm={3} lg={3}>
          <StatCard label="Usuários ativos" value={usuarios.ativos} hint={`${usuarios.pendentes} pendentes · ${usuarios.inativos} inativos`} icon={<GroupsRoundedIcon />} tone="info" />
        </Grid>
        <Grid item xs={6} sm={3} lg={3}>
          <StatCard label="Mensalistas" value={usuarios.mensalistas} hint={`${usuarios.isentos} isentos`} icon={<BadgeRoundedIcon />} tone="brand" />
        </Grid>
        <Grid item xs={6} sm={3} lg={3}>
          <StatCard label="Diaristas" value={usuarios.diaristas} icon={<DirectionsRunRoundedIcon />} tone="neutral" />
        </Grid>
        <Grid item xs={6} sm={3} lg={4}>
          <StatCard label="Em aberto (jogadores)" value={formatCurrency(Math.abs(financeiro.emAberto))} hint={`${financeiro.devedores} devedores`} icon={<WarningAmberRoundedIcon />} tone="gold" />
        </Grid>
        <Grid item xs={6} sm={6} lg={4}>
          <StatCard label="PIX recebido no mês" value={formatCurrency(financeiro.pixRecebidoMes)} hint={`${financeiro.pixPagosMes} pagamentos · ${formatCurrency(financeiro.pixPendente)} pendente`} icon={<PixRoundedIcon />} tone="success" />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <StatCard label="Média de check-ins" value={peladas.mediaCheckins} hint={`nas últimas ${peladas.serie.length} peladas`} icon={<HowToRegRoundedIcon />} tone="info" />
        </Grid>
      </Grid>

      {usuarios.pendentes > 0 && (
        <Alert severity="warning" action={<Button size="small" onClick={() => onGoTab && onGoTab(1)}>Ver usuários</Button>}>
          {usuarios.pendentes} cadastro(s) aguardando aprovação.
        </Alert>
      )}

      <SectionCard title="Check-ins por pelada" subtitle="Jogadores e convidados nas últimas peladas">
        {peladas.serie.length === 0 ? (
          <EmptyState title="Nenhuma pelada registrada" />
        ) : (
          <BarChart
            labels={peladas.serie.map((p) => p.label)}
            series={[
              { label: 'Jogadores', data: peladas.serie.map((p) => p.checkins - p.convidados), color: tokens.color.brand },
              { label: 'Convidados', data: peladas.serie.map((p) => p.convidados), color: tokens.color.gold },
              { label: 'Confirmados sem check-in', data: peladas.serie.map((p) => Math.max(0, p.confirmados - p.checkins)), color: '#CBD5E1' },
            ]}
            stacked
            height={300}
          />
        )}
      </SectionCard>

      <SectionCard
        title="Mensalistas sem jogar e com dívida"
        subtitle="Não fizeram check-in no mês e a mensalidade daquele mês está em aberto (últimos 3 meses)"
        icon={<VolunteerActivismRoundedIcon color="primary" />}
        actions={<Chip size="small" color={abono.jobAtivo ? 'success' : 'default'} label={abono.jobAtivo ? 'Abono automático ativo (dia 1º)' : 'Abono automático desligado'} onClick={() => onGoTab && onGoTab(6)} />}
      >
        {abono.sugestoes.length === 0 ? (
          <EmptyState title="Nenhuma sugestão de abono" description="Todos os mensalistas com dívida jogaram nos meses cobrados." />
        ) : (
          <Stack spacing={1}>
            {abono.sugestoes.map((item) => (
              <Stack key={item.user._id} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
                  <PlayerAvatar src={item.user.foto} name={item.user.nome} size={40} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={600} noWrap>{item.user.nome}</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
                      {item.meses.map((m) => <Chip key={m.referencia} size="small" variant="outlined" label={`Sem jogar em ${m.label}`} />)}
                    </Stack>
                  </Box>
                </Stack>
                <Box sx={{ textAlign: { sm: 'right' }, minWidth: 120 }}>
                  <Typography fontWeight={700} color="error.main">{formatCurrency(item.totalAberto)}</Typography>
                  <Typography variant="caption" color="text.secondary">saldo {formatCurrency(item.saldo)}</Typography>
                </Box>
                <Button variant="contained" size="small" onClick={() => setConfirmar(item)}>Abonar e tornar diarista</Button>
              </Stack>
            ))}
          </Stack>
        )}
      </SectionCard>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <SectionCard title="Maiores devedores" sx={{ height: '100%' }}>
            {financeiro.maioresDevedores.length === 0 ? (
              <EmptyState title="Ninguém devendo" />
            ) : (
              <Stack spacing={1}>
                {financeiro.maioresDevedores.map((d) => (
                  <Stack key={d.user} direction="row" spacing={1.5} alignItems="center">
                    <PlayerAvatar src={d.foto} name={d.nome} size={32} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>{d.nome}</Typography>
                    <Typography variant="body2" fontWeight={700} color="error.main">{formatCurrency(d.saldo)}</Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title="Mensalistas sumidos" subtitle={`Sem check-in nas últimas ${peladas.janelaSemJogar} peladas`} sx={{ height: '100%' }}>
            {peladas.mensalistasSemJogar.length === 0 ? (
              <EmptyState title="Todos os mensalistas jogaram" />
            ) : (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {peladas.mensalistasSemJogar.map((u) => (
                  <Chip key={u.user} avatar={<PlayerAvatar src={u.foto} name={u.nome} size={24} />} label={u.nome.split(' ').slice(0, 2).join(' ')} variant="outlined" />
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title="Aniversariantes do mês" icon={<CakeRoundedIcon sx={{ color: tokens.color.gold }} />} sx={{ height: '100%' }}>
            {aniversariantes.length === 0 ? (
              <EmptyState title="Nenhum aniversariante" />
            ) : (
              <Stack spacing={1}>
                {aniversariantes.map((a) => (
                  <Stack key={a.user} direction="row" spacing={1.5} alignItems="center">
                    <PlayerAvatar src={a.foto} name={a.nome} size={32} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>{a.nome}</Typography>
                    <Chip size="small" label={`dia ${a.dia}`} />
                  </Stack>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <Dialog open={Boolean(confirmar)} onClose={() => setConfirmar(null)}>
        <DialogTitle>Abonar e tornar diarista</DialogTitle>
        <DialogContent>
          {confirmar && (
            <DialogContentText>
              As mensalidades em aberto de <strong>{confirmar.user.nome}</strong> ({confirmar.meses.map((m) => m.label).join(', ')}) no total de <strong>{formatCurrency(confirmar.totalAberto)}</strong> serão abonadas, os PIX pendentes cancelados e o jogador passará a <strong>diarista</strong>. Ele será avisado por e-mail.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmar(null)} color="inherit">Cancelar</Button>
          <Button onClick={abonar} variant="contained" disabled={abonando}>Confirmar abono</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AdminDashboard;
